import {
  Avatar,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
  useToast,
  Badge,
} from '@chakra-ui/react'
import { LuEllipsis, LuTrash2 } from 'react-icons/lu'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  useDeletePatient,
  usePatientsList,
} from '../../../../api/api_hooks/patients'
import EditPatientFormModal from '../../EditPatientFormModal'
import { IPatient } from '../../../../types/patients'
import { useTranslation } from 'react-i18next'
import { getPatientAvatar, getPatientInitials } from '../../../../utils/avatarUtils'

interface IPatientData {
  patientData: IPatient
}

export default function PatientListItem({ patientData }: IPatientData) {
  const toast = useToast()
  const location = useLocation()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutate, isSuccess, isError } = useDeletePatient()
  const { refetch } = usePatientsList()

  const openPatient = () => navigate(`/patients/${patientData.id}${location.search}`)

  const deletePatient = (e: React.MouseEvent) => {
    e.stopPropagation()
    mutate(patientData.id)
  }

  if (isSuccess) refetch()
  if (isError) {
    toast({
      title: t('shared.errors.errorMsg'),
      position: 'top',
      status: 'error',
      duration: 5000,
      isClosable: true,
    })
  }

  const age = moment().diff(moment(patientData?.dateOfBirth), 'years')
  const genderColor = patientData.gender === 'male' ? 'blue' : 'pink'

  return (
    <Box
      bg="white"
      px={6}
      py={4}
      cursor="pointer"
      transition="background 0.15s"
      _hover={{ bg: 'purple.50' }}
      onClick={openPatient}
      role="group"
    >
      <HStack spacing={0}>
        {/* Avatar */}
        <Box w="44px" flexShrink={0} mr={4}>
          <Avatar
            size="sm"
            name={getPatientInitials(patientData.firstName, patientData.lastName)}
            src={getPatientAvatar(patientData.id, patientData.gender)}
            border="2px solid"
            borderColor="gray.100"
          />
        </Box>

        {/* Name */}
        <Box flex={2}>
          <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
            {patientData.firstName} {patientData.lastName}
          </Text>
          {patientData.socialSecurityNumber && (
            <Text fontSize="xs" color="gray.400" fontFamily="mono" noOfLines={1}>
              {patientData.socialSecurityNumber.slice(0, 8)}…
            </Text>
          )}
        </Box>

        {/* Gender */}
        <Box flex={1} display={{ base: 'none', md: 'block' }}>
          <Badge colorScheme={genderColor} variant="subtle" borderRadius="full" fontSize="xs">
            {t(`shared.common.${patientData.gender}`)}
          </Badge>
        </Box>

        {/* DOB */}
        <Box flex={1} display={{ base: 'none', lg: 'block' }}>
          <Text fontSize="sm" color="gray.700">
            {moment(patientData?.dateOfBirth).format('DD MMM YYYY')}
          </Text>
          <Text fontSize="xs" color="gray.400">{age} {t('shared.common.yearsOld')}</Text>
        </Box>

        {/* Address */}
        <Box flex={2} display={{ base: 'none', xl: 'block' }}>
          <Text fontSize="sm" color="gray.500" noOfLines={1}>
            {patientData.address || '—'}
          </Text>
        </Box>

        {/* Actions */}
        <Box w="40px" flexShrink={0} onClick={(e) => e.stopPropagation()}>
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <IconButton
                aria-label="Options"
                icon={<Icon as={LuEllipsis} boxSize={4} />}
                variant="ghost"
                size="sm"
                colorScheme="gray"
                borderRadius="lg"
                opacity={0}
                _groupHover={{ opacity: 1 }}
                transition="opacity 0.15s"
              />
            </PopoverTrigger>
            <Portal>
              <PopoverContent w="160px" borderRadius="xl" boxShadow="xl" border="1px solid" borderColor="gray.100" py={1}>
                <PopoverBody p={1}>
                  <VStack spacing={0} align="stretch">
                    <EditPatientFormModal id={patientData.id} />
                    <Button
                      leftIcon={<Icon as={LuTrash2} boxSize={4} />}
                      variant="ghost"
                      size="sm"
                      colorScheme="red"
                      justifyContent="flex-start"
                      borderRadius="lg"
                      fontWeight="400"
                      fontSize="sm"
                      onClick={deletePatient}
                    >
                      {t('shared.common.delete')}
                    </Button>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        </Box>
      </HStack>
    </Box>
  )
}
